/**
 * Discovery Questionnaire Component
 * ==================================
 * Displays the questionnaire structure with questions grouped by section.
 * Shows responses if available from engagement questionnaire data.
 * Includes confidence badges and source reference tooltips for AI-generated answers.
 */

import { useState, useEffect, useCallback } from 'react';
import { leadAPI } from '../../api';
import { Badge } from '../ui';
import { ConfirmationModal } from '../ui/Modal';
import { useToast } from '../../contexts/ToastContext';
import { usePermissions } from '../../hooks/usePermissions';
import { validateResponse, debounce } from '../../utils/validation';
import { updateResponse, getQuestionnaire } from '../../api/questionnaire';
import CompletionDashboard from '../questionnaire/CompletionDashboard';
import EditResponseModal from '../questionnaire/EditResponseModal';
import VersionHistoryModal from '../questionnaire/VersionHistoryModal';

// Confidence badge component with color coding
const ConfidenceBadge = ({ score }) => {
  if (score === null || score === undefined) return null;

  const percentage = Math.round(score * 100);

  let variant, label;
  if (score >= 0.8) {
    variant = 'success';
    label = 'High';
  } else if (score >= 0.5) {
    variant = 'warning';
    label = 'Medium';
  } else {
    variant = 'danger';
    label = 'Low';
  }

  return (
    <Badge variant={variant} className="text-xs">
      {label} ({percentage}%)
    </Badge>
  );
};

// Source reference tooltip component
const SourceTooltip = ({ sourceReferences, children }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  if (!sourceReferences || sourceReferences.length === 0) {
    return children;
  }

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        className="cursor-help"
      >
        {children}
      </div>
      {showTooltip && (
        <div className="absolute z-50 bottom-full left-0 mb-2 w-80 bg-gray-900 text-white text-xs rounded-lg p-3 shadow-lg">
          <div className="font-medium mb-2 text-gray-300">Source References:</div>
          {sourceReferences.map((ref, idx) => (
            <div key={idx} className="mb-2 last:mb-0">
              {ref.speaker && (
                <div className="text-blue-300 font-medium">{ref.speaker}</div>
              )}
              {ref.source && !ref.email_subject && (
                <div className="text-primary-300 font-medium">{ref.source}</div>
              )}
              {ref.email_subject && (
                <div className="text-green-300 font-medium">Email: {ref.email_subject}</div>
              )}
              {ref.email_sender && (
                <div className="text-green-400 text-[10px]">From: {ref.email_sender}</div>
              )}
              {ref.email_date && (
                <div className="text-gray-500 text-[10px]">{new Date(ref.email_date).toLocaleDateString()}</div>
              )}
              {ref.excerpt && (
                <div className="text-gray-300 italic mt-1">"{ref.excerpt}"</div>
              )}
              {ref.timestamp && (
                <div className="text-gray-500 text-[10px] mt-1">@ {ref.timestamp}</div>
              )}
            </div>
          ))}
          <div className="absolute bottom-0 left-4 transform translate-y-full">
            <div className="border-8 border-transparent border-t-gray-900"></div>
          </div>
        </div>
      )}
    </div>
  );
};

// Pre-fill indicator badge
const PrefillBadge = ({ source }) => {
  if (!source) return null;

  const sourceLabels = {
    'lead': 'Pre-filled from Lead',
    'lead_data': 'Pre-filled from Lead',
    'lead_research': 'Pre-filled from Research',
    'closeio_email': 'From Email'
  };

  const label = sourceLabels[source] || 'Pre-filled';

  return (
    <Badge variant="info" className="text-[10px]">
      {label}
    </Badge>
  );
};

const DiscoveryQuestionnaire = ({ leadId, questionnaire: initialQuestionnaire, onRefresh, onComplete, latestProposalStatus }) => {
  const toast = useToast();
  const { can } = usePermissions();
  // Read-only roles (viewer, compliance_officer) lack questionnaires:edit, so all
  // response edit affordances are hidden for them (they may still view + see history).
  const canEditResponses = can.editQuestionnaire();
  const [templates, setTemplates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});

  // Local questionnaire state for instant updates
  const [localQuestionnaire, setLocalQuestionnaire] = useState(initialQuestionnaire);

  // Derived: is questionnaire completed (editing locked)
  const isCompleted = localQuestionnaire?.completion_status?.status === 'completed';

  // Whether option/sub-field chips are interactive (locked when completed or read-only role)
  const optionsClickable = !isCompleted && canEditResponses;

  // Inline editing state - keyed by question_id
  const [editingQuestions, setEditingQuestions] = useState({});
  const [draftResponses, setDraftResponses] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [savingQuestions, setSavingQuestions] = useState({});
  const [saveErrors, setSaveErrors] = useState({});

  // Modal state
  const [fullScreenQuestion, setFullScreenQuestion] = useState(null);
  const [versionHistoryQuestion, setVersionHistoryQuestion] = useState(null);

  // Discard changes confirmation modal
  const [discardQuestionId, setDiscardQuestionId] = useState(null);

  // Regeneration state
  const [regenerating, setRegenerating] = useState(false);
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);

  // Filter state
  const [activeFilter, setActiveFilter] = useState(null); // 'answered' | 'pending' | 'review' | null

  // Sync local state when prop changes
  useEffect(() => {
    setLocalQuestionnaire(initialQuestionnaire);
  }, [initialQuestionnaire]);

  // Silent refresh — updates localQuestionnaire without triggering parent loading state
  const refreshLocalQuestionnaire = useCallback(async () => {
    try {
      const response = await getQuestionnaire(leadId);
      if (response.data) {
        setLocalQuestionnaire(response.data);
      }
    } catch (err) {
      console.error('Error refreshing questionnaire:', err);
    }
  }, [leadId]);

  // Fetch questionnaire templates
  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await leadAPI.getQuestionnaireTemplates();
      if (response.data) {
        setTemplates(response.data);
        // Expand first section by default
        if (response.data.sections && response.data.sections.length > 0) {
          setExpandedSections({ 0: true });
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to load questionnaire structure');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  useEffect(() => {
    if (!activeFilter || !templates?.sections) return;
    const sectionsToExpand = {};
    templates.sections.forEach((section) => {
      const filtered = getFilteredQuestions(section.questions);
      if (filtered.length > 0) {
        sectionsToExpand[section.section_number] = true;
      }
    });
    setExpandedSections((prev) => ({ ...prev, ...sectionsToExpand }));
  }, [activeFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Toggle section expansion
  const toggleSection = (sectionNumber) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionNumber]: !prev[sectionNumber]
    }));
  };

  // Expand all sections
  const expandAll = () => {
    if (!templates) return;
    const allExpanded = {};
    templates.sections.forEach(section => {
      allExpanded[section.section_number] = true;
    });
    setExpandedSections(allExpanded);
  };

  // Collapse all sections
  const collapseAll = () => {
    setExpandedSections({});
  };

  // Get response for a question from questionnaire data
  const getResponse = (questionNumber) => {
    if (!localQuestionnaire || !localQuestionnaire.sections) return null;

    for (const section of localQuestionnaire.sections) {
      const question = section.questions?.find(q => q.question_id === questionNumber);
      if (question) {
        return question;
      }
    }
    return null;
  };

  // Get question type badge
  const getTypeBadge = (type) => {
    const typeConfig = {
      text: { variant: 'default', label: 'Text' },
      single_select: { variant: 'info', label: 'Single Select' },
      multi_select: { variant: 'primary', label: 'Multi Select' },
    };
    const config = typeConfig[type] || { variant: 'default', label: type };
    return <Badge variant={config.variant} className="text-xs">{config.label}</Badge>;
  };

  // Get response status badge
  const getStatusBadge = (response) => {
    if (!response) return null;

    if (response.response_text) {
      if (response.needs_review) {
        return <Badge variant="warning" className="text-xs">Needs Review</Badge>;
      }
      if (response.source_type === 'user_edited') {
        return <Badge variant="success" className="text-xs">Verified</Badge>;
      }
      return <Badge variant="success" className="text-xs">Answered</Badge>;
    }
    return <Badge variant="default" className="text-xs">Pending</Badge>;
  };

  // Inline editing handlers
  const handleStartEdit = (questionId, currentResponse) => {
    if (isCompleted || !canEditResponses) return;
    setEditingQuestions(prev => ({ ...prev, [questionId]: true }));
    setDraftResponses(prev => ({ ...prev, [questionId]: currentResponse || '' }));
    setValidationErrors(prev => ({ ...prev, [questionId]: [] }));
    setSaveErrors(prev => ({ ...prev, [questionId]: null }));
  };

  const handleCancelEdit = (questionId) => {
    const hasChanges = draftResponses[questionId] !== (getResponse(questionId)?.response_text || '');
    if (hasChanges) {
      setDiscardQuestionId(questionId);
      return;
    }
    setEditingQuestions(prev => ({ ...prev, [questionId]: false }));
    setDraftResponses(prev => ({ ...prev, [questionId]: '' }));
    setValidationErrors(prev => ({ ...prev, [questionId]: [] }));
  };

  const handleConfirmDiscard = () => {
    if (discardQuestionId !== null) {
      setEditingQuestions(prev => ({ ...prev, [discardQuestionId]: false }));
      setDraftResponses(prev => ({ ...prev, [discardQuestionId]: '' }));
      setValidationErrors(prev => ({ ...prev, [discardQuestionId]: [] }));
      setDiscardQuestionId(null);
    }
  };

  // Click option/sub-field to append it into the response (never overwrites)
  const handleOptionClick = (questionId, label, currentResponse, questionType) => {
    if (isCompleted || !canEditResponses) return;
    if (editingQuestions[questionId]) {
      // Already editing — append to current draft
      const current = draftResponses[questionId] || '';
      const separator = questionType === 'multi_select' ? ', ' : ' ';
      const newValue = current ? `${current}${separator}${label}` : label;
      setDraftResponses(prev => ({ ...prev, [questionId]: newValue }));
    } else {
      // Start editing — append to existing response text
      const existing = currentResponse || '';
      const separator = questionType === 'multi_select' ? ', ' : ' ';
      const newValue = existing ? `${existing}${separator}${label}` : label;
      handleStartEdit(questionId, newValue);
    }
  };

  const handleResponseChange = (questionId, value, isRequired) => {
    setDraftResponses(prev => ({ ...prev, [questionId]: value }));

    // Debounced validation
    debouncedValidate(questionId, value, isRequired);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedValidate = useCallback(
    debounce((questionId, value, isRequired) => {
      const result = validateResponse(questionId, value, isRequired);
      setValidationErrors(prev => ({ ...prev, [questionId]: result.errors }));
    }, 300),
    []
  );

  const handleSaveEdit = async (questionId, isRequired) => {
    if (!canEditResponses) return;
    // Validate before saving
    const value = draftResponses[questionId] || '';
    const result = validateResponse(questionId, value, isRequired);

    if (!result.isValid) {
      setValidationErrors(prev => ({ ...prev, [questionId]: result.errors }));
      return;
    }

    try {
      setSavingQuestions(prev => ({ ...prev, [questionId]: true }));

      // Save the response to the backend
      const response = await updateResponse(leadId, questionId, {
        response_text: value,
        change_reason: 'Inline edit'
      });

      if (response.data) {
        // Fetch updated questionnaire data with recalculated completion status
        const updatedQuestionnaire = await getQuestionnaire(leadId);

        if (updatedQuestionnaire.data) {
          // Update local state with new data (includes updated completion_status)
          setLocalQuestionnaire(updatedQuestionnaire.data);
        }

        // Exit edit mode
        setEditingQuestions(prev => ({ ...prev, [questionId]: false }));
        setDraftResponses(prev => ({ ...prev, [questionId]: '' }));
        setValidationErrors(prev => ({ ...prev, [questionId]: [] }));
      }
    } catch (err) {
      console.error('Failed to save response:', err);
      const errorMessage = err.response?.data?.detail?.error?.message
        || (typeof err.response?.data?.detail === 'string' ? err.response.data.detail : null)
        || err.message
        || 'Failed to save response';
      setSaveErrors(prev => ({ ...prev, [questionId]: typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage) }));
    } finally {
      setSavingQuestions(prev => ({ ...prev, [questionId]: false }));
    }
  };

  // Regenerate questionnaire handler
  const handleRegenerate = async () => {
    setShowRegenerateModal(false);
    try {
      setRegenerating(true);
      await leadAPI.regenerateQuestionnaire(leadId);
      toast.success('Questionnaire regeneration started');
      if (onRefresh) {
        setTimeout(onRefresh, 3000);
      }
    } catch (err) {
      console.error('Failed to regenerate questionnaire:', err);
      toast.error(err.response?.data?.detail || 'Failed to regenerate questionnaire');
    } finally {
      setRegenerating(false);
    }
  };

  const getFilteredQuestions = (questions) => {
    if (!activeFilter) return questions;
    return questions.filter((q) => {
      const response = getResponse(q.question_number);
      if (activeFilter === 'answered') {
        return response?.response_text && !response?.needs_review;
      }
      if (activeFilter === 'pending') {
        // Exclude non-applicable (disabled) questions — they have no response
        // by design, so they must not surface as "pending".
        return response?.is_applicable !== false && !response?.response_text && !response?.needs_review;
      }
      if (activeFilter === 'review') {
        return response?.needs_review === true;
      }
      return true;
    });
  };

  // Calculate section progress (only counting applicable questions)
  const getSectionProgress = (sectionNumber) => {
    if (!localQuestionnaire || !localQuestionnaire.sections) return { answered: 0, total: 0, notApplicable: 0, needsReview: 0 };

    const section = localQuestionnaire.sections.find(s => s.section_number === sectionNumber);
    if (!section || !section.questions) return { answered: 0, total: 0, notApplicable: 0, needsReview: 0 };

    // Only count applicable questions
    const applicableQuestions = section.questions.filter(q => q.is_applicable !== false);
    const notApplicable = section.questions.length - applicableQuestions.length;
    const total = applicableQuestions.length;
    const answered = applicableQuestions.filter(q => q.response_text).length;
    const needsReview = applicableQuestions.filter(q => q.needs_review === true).length;
    return { answered, total, notApplicable, needsReview };
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-gray-500 dark:text-gray-400">Loading questionnaire structure...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={fetchTemplates}
          className="text-primary hover:underline"
        >
          Try again
        </button>
      </div>
    );
  }

  if (!templates || !templates.sections) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 dark:text-gray-400">No questionnaire structure found</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Completion Dashboard */}
      {localQuestionnaire && localQuestionnaire.completion_status && localQuestionnaire.completion_status.status !== 'not_generated' && (
        <CompletionDashboard
          completionStatus={localQuestionnaire.completion_status}
          leadId={leadId}
          onComplete={onComplete || onRefresh}
          onRefresh={refreshLocalQuestionnaire}
          latestProposalStatus={latestProposalStatus}
          activeFilter={activeFilter}
          onFilterChange={setActiveFilter}
        />
      )}

      {/* Last Updated Info */}
      {localQuestionnaire && localQuestionnaire.last_updated_at && (
        <div className="text-sm text-gray-600">
          Last updated: {new Date(localQuestionnaire.last_updated_at).toLocaleString()}
          {localQuestionnaire.last_generated_at && (
            <span className="ml-4">
              Last generated: {new Date(localQuestionnaire.last_generated_at).toLocaleString()}
            </span>
          )}
        </div>
      )}

      {/* Header with expand/collapse controls and regenerate button */}
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <div className="flex items-center flex-wrap gap-x-4 gap-y-1">
          <p className="text-sm text-gray-600">
            {templates.total_questions} questions across {templates.total_sections} sections
          </p>
          {localQuestionnaire && localQuestionnaire.completion_status?.status !== 'not_generated' && (
            <Badge variant={localQuestionnaire.completion_status?.status === 'completed' ? 'success' : 'info'}>
              {localQuestionnaire.completion_status?.completion_percentage || 0}% Complete
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-3">
          {/* Regenerate button */}
          {/* {localQuestionnaire && localQuestionnaire.completion_status?.status !== 'not_generated' && (
            <button
              onClick={() => setShowRegenerateModal(true)}
              disabled={regenerating}
              className={`px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium transition ${
                regenerating
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              {regenerating ? (
                <>
                  <span className="inline-block animate-spin mr-2">⟳</span>
                  Regenerating...
                </>
              ) : (
                <>
                  <span className="mr-2">⟳</span>
                  Regenerate
                </>
              )}
            </button>
          )} */}

          {/* Expand/Collapse controls */}
          <div className="flex gap-2">
            <button
              onClick={expandAll}
              className="text-sm text-primary hover:underline"
            >
              Expand All
            </button>
            <span className="text-gray-300">|</span>
            <button
              onClick={collapseAll}
              className="text-sm text-primary hover:underline"
            >
              Collapse All
            </button>
          </div>
        </div>
      </div>

      {activeFilter && (
        <div className="flex items-center justify-between px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm mb-2">
          <span className="text-blue-800 font-medium">
            Showing {activeFilter === 'answered' ? 'answered' : activeFilter === 'pending' ? 'pending' : 'needs review'} questions
          </span>
          <button
            onClick={() => setActiveFilter(null)}
            className="text-blue-600 hover:text-blue-800 font-medium text-xs"
          >
            Clear Filter ×
          </button>
        </div>
      )}

      {/* Sections */}
      <div className="space-y-3">
        {templates.sections.map((section) => {
          const isExpanded = expandedSections[section.section_number];
          const progress = getSectionProgress(section.section_number);
          const hasQuestionnaire = localQuestionnaire !== null;

          const filteredQuestions = getFilteredQuestions(section.questions);

          if (activeFilter && filteredQuestions.length === 0) return null;

          return (
            <div
              key={section.section_number}
              className="border border-gray-200 rounded-lg overflow-hidden"
            >
              {/* Section Header */}
              <button
                onClick={() => toggleSection(section.section_number)}
                className="w-full flex items-center justify-between p-3 sm:p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                  <div className={`w-7 h-7 sm:w-8 sm:h-8 shrink-0 rounded-lg flex items-center justify-center text-sm sm:text-base text-white font-medium ${
                    section.section_number === 0 ? 'bg-primary-500' :
                    section.section_number <= 2 ? 'bg-blue-500' :
                    section.section_number <= 4 ? 'bg-green-500' :
                    section.section_number <= 6 ? 'bg-orange-500' : 'bg-red-500'
                  }`}>
                    {section.section_number === 0 ? 'P' : section.section_number}
                  </div>
                  <div className="text-left">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium text-gray-900">{section.section_title}</h4>
                      {hasQuestionnaire && progress.needsReview > 0 && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-yellow-100 text-yellow-700 border border-yellow-300">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                          </svg>
                          {progress.needsReview} review
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">
                      {section.question_count} questions
                      {hasQuestionnaire && progress.total > 0 && (
                        <span className="ml-2">
                          ({progress.answered}/{progress.total} answered)
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0 ml-2">
                  {hasQuestionnaire && progress.total > 0 && (
                    <div className="hidden sm:flex items-center gap-3">
                      <span className="text-xs font-medium text-gray-500">
                        {Math.round((progress.answered / progress.total) * 100)}%
                      </span>
                      <div className="w-24 bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all ${
                            (() => {
                              const pct = Math.round((progress.answered / progress.total) * 100);
                              if (pct >= 80) return 'bg-green-500';
                              if (pct >= 50) return 'bg-blue-500';
                              if (pct >= 20) return 'bg-yellow-500';
                              return 'bg-red-500';
                            })()
                          }`}
                          style={{ width: `${(progress.answered / progress.total) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  )}
                  <svg
                    className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>

              {/* Section Questions */}
              {isExpanded && (
                <div className="divide-y divide-gray-100">
                  {filteredQuestions.map((question, index) => {
                    const response = getResponse(question.question_number);
                    const isApplicable = response?.is_applicable !== false;
                    const isPrefilled = response?.source_references?.some(
                      ref => ref.source === 'lead_data' || ref.source === 'lead_research'
                    );

                    return (
                      <div
                        key={question.question_number}
                        className={`p-3 sm:p-4 ${isApplicable ? 'hover:bg-gray-50' : 'bg-gray-50 opacity-60'}`}
                      >
                        <div className="sm:flex sm:items-start sm:gap-3">
                          {/* Question Number — desktop left column */}
                          <div className={`hidden sm:flex flex-shrink-0 w-8 h-8 rounded-full items-center justify-center text-sm font-medium ${
                            isApplicable ? 'bg-gray-100 text-gray-600' : 'bg-gray-200 text-gray-400'
                          }`}>
                            {question.question_number}
                          </div>

                          {/* Question Content */}
                          <div className="flex-1 min-w-0">
                            {/* Question Text */}
                            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-x-2 gap-y-1.5 mb-2">
                              <div className="flex-1 min-w-0">
                                <p className={`font-medium ${isApplicable ? 'text-gray-900' : 'text-gray-500'}`}>
                                  {/* Question Number — mobile inline badge */}
                                  <span className={`sm:hidden inline-flex items-center justify-center w-5 h-5 mr-1.5 rounded-full text-[11px] font-medium align-[3px] ${
                                    isApplicable ? 'bg-gray-100 text-gray-600' : 'bg-gray-200 text-gray-400'
                                  }`}>
                                    {question.question_number}
                                  </span>
                                  {question.question_text}
                                  {question.is_required && isApplicable && (
                                    <span className="text-red-500 ml-1">*</span>
                                  )}
                                </p>
                                {/* Conditional dependency indicator */}
                                {question.depends_on_question && (
                                  <p className="text-xs text-gray-400 mt-0.5">
                                    Depends on Q{question.depends_on_question}
                                    {!isApplicable && ' (Not applicable)'}
                                  </p>
                                )}
                              </div>
                              <div className="flex items-center flex-wrap gap-2 sm:flex-shrink-0">
                                {isPrefilled && <PrefillBadge source={response?.source_references?.[0]?.source} />}
                                {!isApplicable && (
                                  <Badge variant="default" className="text-xs">N/A</Badge>
                                )}
                                {isApplicable && getTypeBadge(question.question_type)}
                                {isApplicable && response && getStatusBadge(response)}
                              </div>
                            </div>

                            {/* Helper Text */}
                            {question.helper_text && isApplicable && (
                              <p className="text-sm text-gray-500 mb-2">
                                {question.helper_text}
                              </p>
                            )}

                            {/* Options for select questions */}
                            {question.options && question.options.length > 0 && isApplicable && (
                              <div className="mt-2">
                                <p className="text-xs text-gray-500 mb-1">Options:{optionsClickable && <span className="text-gray-400"> (click to use)</span>}</p>
                                <div className="flex flex-wrap gap-1">
                                  {question.options.map((option, optIndex) => (
                                    <span
                                      key={optIndex}
                                      onClick={() => handleOptionClick(question.question_number, option.label, question.response_text, question.question_type)}
                                      className={`inline-block px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded transition-colors ${optionsClickable ? 'cursor-pointer hover:bg-primary-100 hover:text-primary-700' : 'cursor-default'}`}
                                      title={optionsClickable ? `Click to use "${option.label}"` : option.label}
                                    >
                                      {option.label}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Response if available and question is applicable */}
                            {isApplicable && (
                              <>
                                {/* Inline Edit Mode */}
                                {editingQuestions[question.question_number] ? (
                                  <div className="mt-3 p-3 rounded-lg border-2 border-blue-300 bg-blue-50">
                                    <div className="mb-2">
                                      <label className="block text-xs font-medium text-blue-900 mb-1">
                                        Edit Response
                                      </label>
                                      <textarea
                                        value={draftResponses[question.question_number] || ''}
                                        onChange={(e) => handleResponseChange(question.question_number, e.target.value, question.is_required)}
                                        className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-0 ${
                                          validationErrors[question.question_number]?.length > 0
                                            ? 'border-red-300 focus:border-red-500'
                                            : 'border-gray-300 focus:border-primary'
                                        }`}
                                        rows={4}
                                        placeholder="Enter your response..."
                                      />
                                      {/* Character count */}
                                      <div className="flex items-center justify-between mt-1">
                                        <div className="text-xs text-gray-500">
                                          {(draftResponses[question.question_number] || '').length} characters
                                        </div>
                                        {/* Validation errors */}
                                        {validationErrors[question.question_number]?.length > 0 && (
                                          <div className="text-xs text-red-600">
                                            {validationErrors[question.question_number][0]}
                                          </div>
                                        )}
                                      </div>
                                    </div>

                                    {/* Save Error */}
                                    {saveErrors[question.question_number] && (
                                      <div className="mb-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                                        <p className="text-sm text-red-800">{saveErrors[question.question_number]}</p>
                                      </div>
                                    )}

                                    {/* Action buttons */}
                                    <div className="flex items-center gap-2">
                                      <button
                                        onClick={() => handleSaveEdit(question.question_number, question.is_required)}
                                        disabled={savingQuestions[question.question_number] || validationErrors[question.question_number]?.length > 0}
                                        className={`px-3 py-1.5 rounded text-sm font-medium transition ${
                                          savingQuestions[question.question_number] || validationErrors[question.question_number]?.length > 0
                                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                            : 'bg-blue-600 text-white hover:bg-blue-700'
                                        }`}
                                      >
                                        {savingQuestions[question.question_number] ? 'Saving...' : 'Save'}
                                      </button>
                                      <button
                                        onClick={() => handleCancelEdit(question.question_number)}
                                        disabled={savingQuestions[question.question_number]}
                                        className="px-3 py-1.5 border border-gray-300 rounded text-sm font-medium text-gray-700 hover:bg-gray-50 transition disabled:opacity-50"
                                      >
                                        Cancel
                                      </button>
                                      <button
                                        onClick={() => {
                                          // Pass current draft to full-screen modal
                                          const draft = draftResponses[question.question_number] || '';
                                          setFullScreenQuestion({ ...question, _draftText: draft });
                                          // Clear inline edit without discard prompt
                                          setEditingQuestions(prev => ({ ...prev, [question.question_number]: false }));
                                          setDraftResponses(prev => ({ ...prev, [question.question_number]: '' }));
                                          setValidationErrors(prev => ({ ...prev, [question.question_number]: [] }));
                                        }}
                                        className="ml-auto px-3 py-1.5 border border-gray-300 rounded text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
                                      >
                                        Full Screen
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  /* View Mode */
                                  <>
                                    {response && response.response_text ? (
                                      <div className={`mt-3 p-3 rounded-lg border ${
                                        response.needs_review
                                          ? 'bg-yellow-50 border-yellow-200'
                                          : response.confidence_score >= 0.8
                                            ? 'bg-green-50 border-green-200'
                                            : response.confidence_score >= 0.5
                                              ? 'bg-blue-50 border-blue-200'
                                              : 'bg-orange-50 border-orange-200'
                                      }`}>
                                        <div className="flex items-start justify-between gap-3">
                                          <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-1">
                                              <p className={`text-xs font-medium ${
                                                response.needs_review
                                                  ? 'text-yellow-700'
                                                  : response.confidence_score >= 0.8
                                                    ? 'text-green-700'
                                                    : 'text-blue-700'
                                              }`}>
                                                Response:
                                              </p>
                                              {response.needs_review && (
                                                <Badge variant="warning" className="text-[10px]">
                                                  Needs Review
                                                </Badge>
                                              )}
                                            </div>
                                            <SourceTooltip sourceReferences={response.source_references}>
                                              <p className="text-sm text-gray-800 whitespace-pre-line">
                                                {/* Handle multi-select JSON array display */}
                                                {typeof response.response_text === 'string' &&
                                                response.response_text.startsWith('[')
                                                  ? (() => {
                                                      try {
                                                        const parsed = JSON.parse(response.response_text);
                                                        return Array.isArray(parsed)
                                                          ? parsed.join(', ')
                                                          : response.response_text;
                                                      } catch {
                                                        return response.response_text;
                                                      }
                                                    })()
                                                  : /* Labeled-facts answers (e.g. primary contact:
                                                       "Name: X / Title: Y / Email: Z") render one field
                                                       per line. Detected by structure so it is not tied to
                                                       a specific question number or ordering. */
                                                    (() => {
                                                      const text = response.response_text;
                                                      if (typeof text !== 'string' || !text.includes(' / ')) {
                                                        return text;
                                                      }
                                                      const segments = text.split(' / ');
                                                      const looksLikeLabeledFacts =
                                                        segments.length >= 2 &&
                                                        segments.every(seg => /^[^:]{1,40}:\s/.test(seg));
                                                      return looksLikeLabeledFacts ? segments.join('\n') : text;
                                                    })()}
                                              </p>
                                            </SourceTooltip>
                                          </div>
                                          <div className="flex flex-col items-end gap-1">
                                            <ConfidenceBadge score={response.confidence_score} />
                                          </div>
                                        </div>
                                        <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-200/50">
                                          <p className="text-xs text-gray-500">
                                            Source: {response.source_type?.replace(/_/g, ' ')}
                                            {response.last_updated_at && (
                                              <span> · {new Date(response.last_updated_at).toLocaleString()}</span>
                                            )}
                                          </p>
                                          {response.source_references && response.source_references.length > 0 && (
                                            <p className="text-xs text-gray-400">
                                              {response.source_references.length} reference{response.source_references.length > 1 ? 's' : ''}
                                              {response.source_references[0]?.source === 'meeting_notes'
                                                ? ' from meeting notes'
                                                : response.source_references[0]?.email_subject
                                                  ? ` from email: "${response.source_references[0].email_subject}"`
                                                  : <>
                                                      {response.source_references[0]?.speaker &&
                                                        ` from ${response.source_references[0].speaker}`}
                                                      {response.source_references[0]?.source &&
                                                        ` (${response.source_references[0].source.replace(/_/g, ' ')})`}
                                                    </>
                                              }
                                            </p>
                                          )}
                                        </div>

                                        {/* Edit and History buttons */}
                                        <div className="flex items-center gap-2 mt-3 pt-2 border-t border-gray-200/50">
                                          {!isCompleted && canEditResponses && (
                                            <button
                                              onClick={() => handleStartEdit(question.question_number, response.response_text)}
                                              className="px-3 py-1 text-xs font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition"
                                            >
                                              ✏️ Edit
                                            </button>
                                          )}
                                          <button
                                            onClick={() => setVersionHistoryQuestion(question)}
                                            className="px-3 py-1 text-xs font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded transition"
                                          >
                                            📜 History
                                          </button>
                                        </div>
                                      </div>
                                    ) : (
                                      /* No response yet - show Add button */
                                      <div className="mt-3">
                                        {!isCompleted && canEditResponses && (
                                          <button
                                            onClick={() => handleStartEdit(question.question_number, '')}
                                            className="px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition"
                                          >
                                            + Add Response
                                          </button>
                                        )}
                                      </div>
                                    )}
                                  </>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Modals */}
      {fullScreenQuestion && (
        <EditResponseModal
          question={fullScreenQuestion}
          leadId={leadId}
          onClose={() => setFullScreenQuestion(null)}
          onSave={async () => {
            setFullScreenQuestion(null);
            try {
              const updatedQuestionnaire = await getQuestionnaire(leadId);
              if (updatedQuestionnaire.data) {
                setLocalQuestionnaire(updatedQuestionnaire.data);
              }
            } catch (err) {
              console.error('Failed to refresh questionnaire after modal save:', err);
            }
          }}
        />
      )}

      {versionHistoryQuestion && (
        <VersionHistoryModal
          questionId={versionHistoryQuestion.question_id}
          questionText={versionHistoryQuestion.question_text}
          leadId={leadId}
          onClose={() => setVersionHistoryQuestion(null)}
        />
      )}

      {/* Regenerate Confirmation Modal */}
      {showRegenerateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            <div className="px-6 py-5 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Regenerate Questionnaire</h3>
                  <p className="text-sm text-gray-500">This action cannot be undone</p>
                </div>
              </div>
            </div>
            <div className="px-6 py-4">
              <p className="text-sm text-gray-600">
                Are you sure you want to regenerate the questionnaire? This will use the latest meeting transcripts and research data to update existing responses.
              </p>
            </div>
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-3">
              <button
                onClick={() => setShowRegenerateModal(false)}
                className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleRegenerate}
                className="px-5 py-2.5 text-sm font-semibold text-white bg-primary rounded-lg hover:bg-primary-600 transition-colors"
              >
                Regenerate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Discard Changes Confirmation Modal */}
      <ConfirmationModal
        isOpen={discardQuestionId !== null}
        onClose={() => setDiscardQuestionId(null)}
        onConfirm={handleConfirmDiscard}
        title="Discard Changes"
        message="You have unsaved changes. Are you sure you want to discard them?"
        confirmText="Discard"
        cancelText="Keep Editing"
        confirmVariant="primary"
      />
    </div>
  );
};

export default DiscoveryQuestionnaire;
